import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Plus, X, Upload } from 'lucide-react'
import { toast } from "sonner"
import { uploadProduct } from "@/utils/helper/product"
import Loader from "@/components/loader/loader"
import MediaSelectorModal from "../library/media-selector"

// Subcategory enums
const SUBCATEGORIES = {
  Skin: [
    "Serums",
    "Moisturizers",
    "Cleansers",
    "Masks",
    "Treatments",
    "Sun Care",
    "Toners",
    "Exfoliators",
    "Conditioners",
    "Oils",
    "Butters",
  ],
  Hair: [
    "Shampoos",
    "Conditioners",
    "Hair Oils",
    "Hair Masks",
    "Hair Treatments",
    "Hair Serums",
    "Styling Products",
    "Oils",
    "Butters",
  ],
}

export default function AdminProductCreatePage() {
  const navigate = useNavigate()
  const [product, setProduct] = useState({
    name: "",
    category: "",
    subcategory: "",
    price: "",
    short_description: "",
    description: "",
    sku: "",
    stock_quantity: "",
    weight: "",
    discount: "",
    status: "active",
    ingredients: "",
    key_benefits: "",
    skin_type: "",
    usage_instructions: "",
    warnings: "",
    variations: [],
    selectedImages: [],
  })

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false)

  const handleChange = (e) => {
    const { id, value } = e.target
    setProduct((prev) => ({ ...prev, [id]: value }))
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: "" }))
  }

  const handleSelectChange = (id, value) => {
    setProduct((prev) => {
      const updated = { ...prev, [id]: value }
      // Clear subcategory when category changes
      if (id === "category") {
        updated.subcategory = ""
      }
      return updated
    })
  }


  const removeImage = (index) => {
    setProduct((prev) => ({
      ...prev,
      selectedImages: prev.selectedImages.filter((_, i) => i !== index),
    }))
  }

  const handleMediaSelect = (files) => {
    if (files && files.length > 0) {
      // Convert selected files to the format expected by the product state
      const selectedImages = files.map(file => ({
        id: file.id,
        name: file.name,
        url: file.url,
        type: file.type,
        size: file.size,
      }))

      setProduct((prev) => ({
        ...prev,
        selectedImages: [...prev.selectedImages, ...selectedImages],
      }))
    }
    setIsMediaModalOpen(false)
  }

  const addVariation = () => {
    setProduct((prev) => ({
      ...prev,
      variations: [...prev.variations, { size: "", price: "", weight: "", stock_quantity: "", sku: "" }],
    }))
  }

  const handleVariationChange = (index, field, value) => {
    const newVariations = [...product.variations]
    newVariations[index][field] = value
    setProduct((prev) => ({ ...prev, variations: newVariations }))
  }

  const removeVariation = (index) => {
    setProduct((prev) => ({
      ...prev,
      variations: prev.variations.filter((_, i) => i !== index),
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Validate required fields
      if (!product.name || !product.category || !product.price) {
        toast.error("Please fill in all required fields (Name, Category, Price)")
        setIsSubmitting(false)
        return
      }

      // Validate price is a valid number
      const price = parseFloat(product.price)
      if (isNaN(price) || price < 0) {
        toast.error("Please enter a valid price")
        setIsSubmitting(false)
        return
      }

      // Validate stock quantity if provided
      if (product.stock_quantity && (isNaN(parseInt(product.stock_quantity)) || parseInt(product.stock_quantity) < 0)) {
        toast.error("Please enter a valid stock quantity")
        setIsSubmitting(false)
        return
      }

      // Format imagePaths - extract URLs from selected images
      const imagePaths = product.selectedImages
        .map((image) => image.url)
        .filter(Boolean)

      // Format variations - filter out empty variations and convert to backend format
      const validVariations = product.variations
        .filter((v) => v.size || v.price || v.stock_quantity)
        .map((v) => ({
          size: v.size || null,
          scent: v.scent || null,
          weight: v.weight ? parseFloat(v.weight) : null,
          price: v.price ? parseFloat(v.price) : 0,
          stock_quantity: v.stock_quantity ? parseInt(v.stock_quantity) : 0,
          sku: v.sku || null,
        }))

      // Prepare data for backend
      const productData = {
        name: product.name.trim(),
        category: product.category,
        subcategory: product.subcategory || null,
        short_description: product.short_description || null,
        description: product.description || null,
        ingredients: product.ingredients || null,
        key_benefits: product.key_benefits || null,
        skin_type: product.skin_type || null,
        usage_instructions: product.usage_instructions || null,
        warnings: product.warnings || null,
        price: price,
        discount: product.discount ? parseFloat(product.discount) : 0,
        stock_quantity: product.stock_quantity ? parseInt(product.stock_quantity) : 0,
        sku: product.sku || null,
        weight: product.weight ? parseFloat(product.weight) : null,
        status: product.status || "active",
        ...(validVariations.length > 0 && { variations: JSON.stringify(validVariations) }),
        ...(imagePaths.length > 0 && { imagePaths: JSON.stringify(imagePaths) }),
      }

      // Call API
      const response = await uploadProduct(productData)
      
      toast.success(response.message || "Product created successfully!")
      
      // Navigate to product list or product edit page
      if (response.productId) {
        navigate(`/admin/products/edit/${response.productId}`)
      } else {
        navigate("/admin/products/list")
      }
    } catch (error) {
      console.error("Error creating product:", error)
      const errorMessage = error.response?.data?.error?.message || error.message || "Failed to create product. Please try again."
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6 md:p-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-2">
              Create New Product
            </h1>
            <p className="text-muted-foreground">Add a new premium skincare product to your collection</p>
          </div>
          <Button
            type="submit"
            form="product-form"
            disabled={isSubmitting}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-6 py-2 rounded-lg shadow-md hover:shadow-lg transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2 inline-block" />
                Creating...
              </>
            ) : (
              "Create Product"
            )}
          </Button>
        </div>

        <form onSubmit={handleSubmit} id="product-form" className="space-y-6">
          <div>
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-5 rounded-none border-b bg-card">
                <TabsTrigger value="basic" className="rounded-none">Basic</TabsTrigger>
                <TabsTrigger value="pricing" className="rounded-none">Pricing</TabsTrigger>
                <TabsTrigger value="details" className="rounded-none">Details</TabsTrigger>
                <TabsTrigger value="images" className="rounded-none">Images</TabsTrigger>
                <TabsTrigger value="variations" className="rounded-none">Variations</TabsTrigger>
              </TabsList>

              {/* Basic Information Tab */}
              <TabsContent value="basic" className="p-6">
                <div className="grid gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Basic Information</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="name" className="font-semibold text-foreground">
                        Product Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="e.g., Moisturizing Face Cream"
                        value={product.name}
                        onChange={handleChange}
                        maxLength={255}
                        required
                        className="border-border focus:ring-primary"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="category" className="font-semibold text-foreground">
                        Category <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={product.category}
                        onValueChange={(value) => handleSelectChange("category", value)}
                        required
                      >
                        <SelectTrigger id="category" className="border-border focus:ring-primary">
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Skin">Skin</SelectItem>
                          <SelectItem value="Hair">Hair</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Subcategory Field */}
                  {product.category && (
                    <div className="grid gap-2">
                      <Label htmlFor="subcategory" className="font-semibold text-foreground">
                        Subcategory (Optional)
                      </Label>
                      <Select
                        value={product.subcategory || "none"}
                        onValueChange={(value) => handleSelectChange("subcategory", value === "none" ? "" : value)}
                      >
                        <SelectTrigger id="subcategory" className="border-border focus:ring-primary">
                          <SelectValue placeholder="Select a subcategory (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {SUBCATEGORIES[product.category]?.map((subcat) => (
                            <SelectItem key={subcat} value={subcat}>
                              {subcat}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="grid gap-2">
                    <Label htmlFor="short_description" className="font-semibold text-foreground">
                      Short Description
                    </Label>
                    <Input
                      id="short_description"
                      type="text"
                      placeholder="Brief description (max 500 chars)"
                      value={product.short_description}
                      onChange={handleChange}
                      maxLength={500}
                      className="border-border focus:ring-primary"
                    />
                    <p className="text-xs text-muted-foreground">{product.short_description.length}/500</p>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="description" className="font-semibold text-foreground">
                      Description
                    </Label>
                    <Textarea
                      id="description"
                      placeholder="Detailed product description..."
                      value={product.description}
                      onChange={handleChange}
                      rows={4}
                      className="border-border focus:ring-primary resize-none"
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Pricing & Inventory Tab */}
              <TabsContent value="pricing" className="p-6">
                <div className="grid gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Pricing & Inventory</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="price" className="font-semibold text-foreground">
                        Price (₦) <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="price"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={product.price}
                        onChange={handleChange}
                        min="0"
                        required
                        className="border-border focus:ring-primary"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="discount" className="font-semibold text-foreground">
                        Discount (%)
                      </Label>
                      <Input
                        id="discount"
                        type="number"
                        step="0.01"
                        placeholder="0"
                        value={product.discount}
                        onChange={handleChange}
                        min="0"
                        max="100"
                        className="border-border focus:ring-primary"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="weight" className="font-semibold text-foreground">
                        Weight (kg)
                      </Label>
                      <Input
                        id="weight"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={product.weight}
                        onChange={handleChange}
                        min="0"
                        className="border-border focus:ring-primary"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="stock_quantity" className="font-semibold text-foreground">
                        Stock Quantity <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="stock_quantity"
                        type="number"
                        placeholder="0"
                        value={product.stock_quantity}
                        onChange={handleChange}
                        min="0"
                        required
                        className="border-border focus:ring-primary"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="sku" className="font-semibold text-foreground">
                        SKU (Stock Keeping Unit)
                      </Label>
                      <Input
                        id="sku"
                        type="text"
                        placeholder="e.g., SKU-001"
                        value={product.sku}
                        onChange={handleChange}
                        maxLength={100}
                        className="border-border focus:ring-primary"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="status" className="font-semibold text-foreground">
                        Status
                      </Label>
                      <Select value={product.status} onValueChange={(value) => handleSelectChange("status", value)}>
                        <SelectTrigger id="status" className="border-border focus:ring-primary">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                          <SelectItem value="draft">Draft</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Product Details Tab */}
              <TabsContent value="details" className="p-6">
                <div className="grid gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Product Details</h3>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="ingredients" className="font-semibold text-foreground">
                      Ingredients
                    </Label>
                    <Textarea
                      id="ingredients"
                      placeholder="List all ingredients..."
                      value={product.ingredients}
                      onChange={handleChange}
                      rows={3}
                      className="border-border focus:ring-primary resize-none"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="key_benefits" className="font-semibold text-foreground">
                      Key Benefits
                    </Label>
                    <Textarea
                      id="key_benefits"
                      placeholder="e.g., Hydrates, Nourishes, Protects"
                      value={product.key_benefits}
                      onChange={handleChange}
                      rows={3}
                      className="border-border focus:ring-primary resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="skin_type" className="font-semibold text-foreground">
                        Skin Type
                      </Label>
                      <Select
                        value={product.skin_type}
                        onValueChange={(value) => handleSelectChange("skin_type", value)}
                      >
                        <SelectTrigger id="skin_type" className="border-border focus:ring-primary">
                          <SelectValue placeholder="Select applicable skin types" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Skin Types</SelectItem>
                          <SelectItem value="dry">Dry</SelectItem>
                          <SelectItem value="oily">Oily</SelectItem>
                          <SelectItem value="combination">Combination</SelectItem>
                          <SelectItem value="sensitive">Sensitive</SelectItem>
                          <SelectItem value="acne_prone">Acne-Prone</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="usage_instructions" className="font-semibold text-foreground">
                      Usage Instructions
                    </Label>
                    <Textarea
                      id="usage_instructions"
                      placeholder="e.g., Apply twice daily..."
                      value={product.usage_instructions}
                      onChange={handleChange}
                      rows={3}
                      className="border-border focus:ring-primary resize-none"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="warnings" className="font-semibold text-foreground">
                      Warnings
                    </Label>
                    <Textarea
                      id="warnings"
                      placeholder="e.g., For external use only"
                      value={product.warnings}
                      onChange={handleChange}
                      rows={2}
                      className="border-border focus:ring-primary resize-none"
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Product Images Tab */}
              <TabsContent value="images" className="p-6">
                <div className="grid gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Product Images</h3>
                    <p className="text-sm text-muted-foreground">Upload up to 10 high-quality images</p>
                  </div>

                  <div className="border-2 border-dashed border-border rounded-lg p-8 hover:border-primary transition-colors cursor-pointer bg-card">
                    <div className="flex flex-col items-center gap-2 cursor-pointer" onClick={() => setIsMediaModalOpen(true)}>
                      <div className="p-3 bg-muted rounded-lg">
                        <Upload className="h-6 w-6 text-primary" />
                      </div>
                      <span className="font-semibold text-foreground">Select from Media Library</span>
                      <span className="text-sm text-muted-foreground">Choose images from your uploaded media</span>
                    </div>
                  </div>

                  {product.selectedImages.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {product.selectedImages.map((image, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={image.url || "/placeholder.svg"}
                            alt={`Selected image ${index + 1}`}
                            className="h-24 w-24 object-cover rounded-lg border border-border"
                            onError={(e) => {
                              e.target.src = "/placeholder.svg"
                            }}
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute -top-2 -right-2 h-6 w-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => removeImage(index)}
                          >
                            <X className="h-3 w-3" />
                            <span className="sr-only">Remove image</span>
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Variations Tab */}
              <TabsContent value="variations" className="p-6">
                <div className="grid gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Product Variations</h3>
                    <p className="text-sm text-muted-foreground">Add different sizes, weights, or prices</p>
                  </div>

                  {product.variations.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No variations added yet</p>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {product.variations.map((variation, index) => (
                        <div
                          key={index}
                          className="p-4 border border-border rounded-lg hover:border-primary transition-colors bg-card"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                            <div className="grid gap-1">
                              <Label className="text-xs font-semibold text-foreground">Size</Label>
                              <Input
                                placeholder="e.g., 50ml"
                                value={variation.size}
                                onChange={(e) => handleVariationChange(index, "size", e.target.value)}
                                className="border-border focus:ring-primary"
                              />
                            </div>
                            <div className="grid gap-1">
                              <Label className="text-xs font-semibold text-foreground">Price (₦)</Label>
                              <Input
                                placeholder="0.00"
                                type="number"
                                step="0.01"
                                value={variation.price}
                                onChange={(e) => handleVariationChange(index, "price", e.target.value)}
                                className="border-border focus:ring-primary"
                              />
                            </div>
                            <div className="grid gap-1">
                              <Label className="text-xs font-semibold text-foreground">Weight (kg)</Label>
                              <Input
                                placeholder="0.00"
                                type="number"
                                step="0.01"
                                value={variation.weight}
                                onChange={(e) => handleVariationChange(index, "weight", e.target.value)}
                                className="border-border focus:ring-primary"
                              />
                            </div>
                            <div className="grid gap-1">
                              <Label className="text-xs font-semibold text-foreground">Stock</Label>
                              <Input
                                placeholder="0"
                                type="number"
                                value={variation.stock_quantity}
                                onChange={(e) => handleVariationChange(index, "stock_quantity", e.target.value)}
                                className="border-border focus:ring-primary"
                              />
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeVariation(index)}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                              <X className="h-4 w-4" />
                              <span className="sr-only">Remove variation</span>
                            </Button>
                          </div>
                          <div className="grid gap-1 mt-3">
                            <Label className="text-xs font-semibold text-foreground">SKU</Label>
                            <Input
                              placeholder="e.g., SKU-001-50ml"
                              value={variation.sku}
                              onChange={(e) => handleVariationChange(index, "sku", e.target.value)}
                              className="border-border focus:ring-primary"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <Button
                    type="button"
                    onClick={addVariation}
                    variant="outline"
                    className="w-full border-border text-primary hover:bg-muted hover:text-primary"
                  >
                    <Plus className="h-4 w-4 mr-2" /> Add Variation
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </form>
      </div>

      {/* Media Selector Modal */}
      <MediaSelectorModal
        isOpen={isMediaModalOpen}
        onClose={() => setIsMediaModalOpen(false)}
        onSelect={handleMediaSelect}
        multiple={true}
        fileTypes={["image"]}
        title="Select Product Images"
        description="Choose images from your media library for this product"
      />
    </div>
  )
}
